/**
 * Responsibility: declare the site's navigation and wordmark for the kit shell.
 *
 * The shell is driven entirely by this object, so adding a page is an entry
 * here rather than an edit to any layout component.
 *
 * Order is by how often the operator needs it during a drop, not by how the
 * system is built: the watchlist is what you act on, Live is what you stare at,
 * and Nodes is the one you check before a drop rather than during it.
 */
import ViewListIcon from '@mui/icons-material/ViewList';
import BoltIcon from '@mui/icons-material/Bolt';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import NotificationsIcon from '@mui/icons-material/NotificationsActive';
import RadarIcon from '@mui/icons-material/Radar';
import DiamondIcon from '@mui/icons-material/Diamond';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import SearchIcon from '@mui/icons-material/Search';
import InventoryIcon from '@mui/icons-material/Inventory2';
import FavoriteIcon from '@mui/icons-material/FavoriteBorder';
import CameraAltIcon from '@mui/icons-material/CameraAltOutlined';
import SettingsIcon from '@mui/icons-material/SettingsOutlined';
import VisibilityIcon from '@mui/icons-material/VisibilityOutlined';
import type { BrandConfig, NavConfig } from '@kit/layout';

export const brand: BrandConfig = {
  name: 'dropmon',
  tagline: 'retail drop monitor',
  icon: <RadarIcon fontSize="small" />,
  href: '/watchlist',
};

export const nav: NavConfig = {
  sections: [
    {
      label: 'Monitoring',
      items: [
        {
          label: 'Watchlist',
          href: '/watchlist',
          icon: <ViewListIcon fontSize="small" />,
          primary: true,
        },
        { label: 'Live', href: '/live', icon: <BoltIcon fontSize="small" />, primary: true },
      ],
    },
    {
      label: 'Intelligence',
      items: [
        {
          label: 'News',
          href: '/news',
          icon: <NewspaperIcon fontSize="small" />,
          primary: true,
        },
        {
          label: 'Alerts',
          href: '/alerts',
          icon: <NotificationsIcon fontSize="small" />,
          primary: true,
        },
      ],
    },
    {
      // Named for what it is for rather than for one of the algorithms in
      // it. Ordered by how a card moves through the system: you find it,
      // the board finds it for you, you set it aside, you decide whether
      // to grade it.
      label: 'Trading',
      items: [
        {
          label: 'Search',
          href: '/search',
          icon: <SearchIcon fontSize="small" />,
          primary: true,
        },
        {
          label: 'Discovery',
          href: '/discovery',
          icon: <TravelExploreIcon fontSize="small" />,
          primary: true,
        },
        {
          label: 'Watching',
          href: '/watching',
          icon: <VisibilityIcon fontSize="small" />,
          primary: true,
        },
        {
          label: 'Grading',
          href: '/grading',
          icon: <DiamondIcon fontSize="small" />,
          primary: true,
        },
      ],
    },
    {
      // Last, and apart from Trading: these are about what is already
      // yours rather than about finding anything. Neither answers a
      // question you ask during a hunt.
      label: 'Personal',
      items: [
        {
          label: 'My collection',
          href: '/collection',
          icon: <InventoryIcon fontSize="small" />,
          primary: true,
        },
        {
          label: 'Likes',
          href: '/likes',
          icon: <FavoriteIcon fontSize="small" />,
          primary: true,
        },
        {
          // Above Settings because it is a thing you do, not a thing you
          // configure - and on a phone it is the reason the site is open.
          label: 'Scan',
          href: '/scan',
          icon: <CameraAltIcon fontSize="small" />,
          primary: true,
        },
      ],
    },
    {
      // Its own section rather than the tail of Personal. Everything above
      // is a thing you look at; this is the account behind them - who is
      // signed in, and the credentials the hub uses on your behalf. Those
      // are a different kind of answer and belong under their own heading.
      label: 'Account',
      items: [
        {
          label: 'Settings',
          href: '/settings',
          icon: <SettingsIcon fontSize="small" />,
        },
      ],
    },
  ],
};
